import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { camelize, decamelize, camelizeKeys, decamelizeKeys } from 'humps';
import { List, Map } from 'immutable';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';
import moment from 'moment';

import config from '../../../config';
import Grid from '../../helpers/AgGridTable';
import DropDown, { DropDownElement } from '../../helpers/DropDown';
import { showError, showInformation, showInformationCustomTitle } from '../../../utils/MessagePopup';
import { clickIfNotDouble } from '../../../utils/doubleClickCheck';
import checkActionPermission from '../../../utils/browsePermission';
import { targetTableActions } from '../../../utils/factories';
import { successWindwardGeneration } from '../../../utils/successWindwardGeneration';
import { DELETE_FAIL_TEXT, DELETE_SUCCESSFULLY_TEXT, DELETION_REQUEST_TEXT } from '../../../config/constants/common';
import { handleAddSaveQuery } from '../../../actions/browse';
import { handleSortModelChanged } from '../../../utils/sorting';
import { usePopupContext } from '../../../contexts/PopupContext';
import { ProjectCell } from '../ProjectCell';
import { CheckboxCellRenderer } from '../CheckboxCellRenderer';
import { ApprovedCellRenderer } from '../ApprovedCellRenderer';
import { Titled } from '../Titled';
import { NextActionCellRenderer } from '../NextActionCellRenderer';
import { Colored } from '../Colored';
import { Card } from '../../helpers/Card';
import { IconButton } from '../../helpers/IconButton';
import { Substring } from '../Substring';
import Button from '../../helpers/Button';

import style from './style.module.css';

const getRowNodeId = data => data.order?.toString();

const BrowseTable = props => {
  const {
    backloggedActivities,
    columnDefs,
    data,
    handleAddSaveQuery,
    handleResetSelection,
    handleUpdateAllRows,
    handleUpdateSelectedRow,
    hasParams,
    hideContextDropDown,
    location,
    name,
    onAbortRequest,
    onAddTag,
    onChange,
    onClearFilter,
    onConfirmMerging,
    onDelete,
    onDeleteConfirmation,
    onDoubleClickRow,
    onDownloadExport,
    onGetNextPageData,
    onGetPageData,
    onGridReady: onGridReadyProp,
    onPushNextActionsDates,
    onRowChange,
    onSaveAddUpdateBuyer,
    onSelectRows,
    onSetNextActions,
    onToggle,
    onUpdateQueryResult,
    requestParams,
    runWindwardReport,
    setBacklog,
    stateParams,
    updateIndustryTags,
    user,
  } = props;

  const popupContext = usePopupContext();

  const [selectedAllOnce, setSelectedAllOnce] = useState(false);

  const gridApiRef = useRef(null);
  const gridColumnApiRef = useRef(null);
  const prevIsFetchingRef = useRef(data.get('isFetching'));
  const hasMountedRef = useRef(false);

  const contextActionList = useMemo(() => targetTableActions(user.getIn(['roles', 0, 'slug'])), [user]);

  const hasAccess = useCallback(() => {
    if (!user) return false;

    return [config.DIRECTOR, config.FINANCE_ADMIN].includes(user.getIn(['roles', 0, 'slug'], null));
  }, [user]);

  const onGridReady = useCallback(
    params => {
      gridApiRef.current = params.api;
      gridColumnApiRef.current = params.columnApi;
      gridApiRef.current.sizeColumnsToFit();

      if (onGridReadyProp) {
        onGridReadyProp(params);
      }
    },
    [onGridReadyProp],
  );

  const handleSelectAll = useCallback(
    event => {
      event.preventDefault();

      if (data.get('queryResults').size > 0) {
        handleUpdateAllRows({
          name,
          field: 'selected',
          value: true,
        });
      }
    },
    [data, handleUpdateAllRows, name],
  );

  const handleDeselectAll = useCallback(
    event => {
      event.preventDefault();

      if (data.get('queryResults').size > 0) {
        handleUpdateAllRows({
          name,
          field: 'selected',
          value: false,
        });
      }
    },
    [data, handleUpdateAllRows, name],
  );

  const handleGetSelectedIds = useCallback(
    () =>
      data.get('selectList').reduce((accumulator, item) => {
        accumulator.push(item.get('id'));

        return accumulator;
      }, []),
    [data],
  );

  const handleCellClickedRaw = useCallback(
    params => {
      const {
        colDef: { field },
        data: { order, selected },
      } = params;

      if (field === 'selected') {
        handleUpdateSelectedRow({
          name,
          index: order,
          field: 'selected',
          value: !selected,
        });
      }
    },
    [handleUpdateSelectedRow, name],
  );

  const handleRowDoubleClickedRaw = useCallback(
    row => {
      onDoubleClickRow(row);
    },
    [onDoubleClickRow],
  );

  const [handleCellClicked, handleRowDoubleClicked] = useMemo(
    () => clickIfNotDouble(handleCellClickedRaw, handleRowDoubleClickedRaw, row => row.rowIndex),
    [handleCellClickedRaw, handleRowDoubleClickedRaw],
  );

  const handleAddTag = useCallback(
    (tag, callback) => {
      onAddTag({
        body: {
          params: {
            tag,
            user_id: user.get('id'),
            selected_ids: handleGetSelectedIds(),
            entityType: name,
            hash: data.get('searchParamsHash'),
          },
        },
        callback,
      });
    },
    [onAddTag, user, handleGetSelectedIds, name, data],
  );

  const handleUpdateNextActions = useCallback(
    (formData, callback) => {
      const params = {
        selected_ids: handleGetSelectedIds(),
        override: formData.get('override'),
        backlogged: formData.get('backlog', false),
        hash: data.get('searchParamsHash'),
      };

      if (formData.get('date')) {
        params.date = formData.get('date').format('YYYY-MM-DD');
      }

      if (formData.getIn(['assignedTo', 'selected'])) {
        params.user_id = formData.getIn(['assignedTo', 'selected', 'id']);
      }

      if (formData.getIn(['activity', 'selected'])) {
        params.activity = formData.getIn(['activity', 'selected', 'value']);
      }

      if (formData.get('description')) {
        params.description = formData.get('description');
      }

      if (formData.getIn(['status', 'index']) > 0) {
        params.statusId = formData.getIn(['status', 'index']);
      }

      onSetNextActions({
        body: {
          params,
        },
        callback,
      });
    },
    [handleGetSelectedIds, data, onSetNextActions],
  );

  const handleDeleteNextActions = useCallback(
    callback => {
      const params = {
        selected_ids: handleGetSelectedIds(),
        na_delete: true,
        hash: data.get('searchParamsHash'),
      };

      onSetNextActions({
        body: {
          params,
        },
        callback,
      });
    },
    [handleGetSelectedIds, data, onSetNextActions],
  );

  const handleUpdateNextActionsDates = useCallback(
    (formData, callback) => {
      const params = {
        selected_ids: handleGetSelectedIds(),
        hash: data.get('searchParamsHash'),
      };

      if (formData.get('forward')) {
        params.push_forward = true;
        params.days = parseInt(formData.get('forwardDays', 0), 10);
      }

      if (formData.get('backward')) {
        params.push_backward = true;
        params.days = parseInt(formData.get('backwardDays', 0), 10);
      }

      if (formData.get('following')) {
        params.set_date = true;
        params.date = formData.get('followingDate', moment()).format('YYYY-MM-DD');
      }

      onPushNextActionsDates({
        body: {
          params,
        },
        callback,
      });
    },
    [handleGetSelectedIds, data, onPushNextActionsDates],
  );

  const handleSaveAddUpdateBuyer = useCallback(
    (formData, callback) => {
      const params = Object.assign(
        {
          selected_ids: handleGetSelectedIds(),
          hash: data.get('searchParamsHash'),
        },
        formData,
      );

      onSaveAddUpdateBuyer({
        body: {
          params,
        },
        callback,
      });
    },
    [handleGetSelectedIds, data, onSaveAddUpdateBuyer],
  );

  const handleDelete = useCallback(
    (deleteProp, successMessage) => {
      deleteProp({
        body: {
          params: {
            selected_ids: handleGetSelectedIds(),
            hash: data.get('searchParamsHash'),
            entityType: name,
          },
        },
        callback: {
          beforeStart: popupContext.startPopupLoading,
          afterSuccess: () => {
            popupContext.endPopupLoading();
            showInformation(popupContext.openPopup, successMessage);
            onUpdateQueryResult(
              name,
              data.get('queryResults').filter(item => item.get('selected') !== true),
            );
            onGetPageData(1, false);
          },
          afterError: () => {
            popupContext.endPopupLoading();
            showError(popupContext.openPopup, [DELETE_FAIL_TEXT]);
          },
        },
      });
    },
    [handleGetSelectedIds, data, name, popupContext, onUpdateQueryResult, onGetPageData],
  );

  const handleUpdateIndustryTags = useCallback(
    (selectedIds, industryCategoryIds) => {
      updateIndustryTags({
        body: {
          params: {
            selected_ids: selectedIds,
            override: false,
            industry_category_ids: industryCategoryIds,
            hash: data.get('searchParamsHash'),
          },
        },
        callback: {
          beforeStart: popupContext.startPopupLoading,
          afterSuccess: () => {
            popupContext.endPopupLoading();
            showInformation(popupContext.openPopup, 'Update Successfully');
            onGetPageData(1, false);
          },
          afterError: () => {
            popupContext.endPopupLoading();
            showError(popupContext.openPopup, ['Update Fail. Please try again!']);
          },
        },
      });
    },
    [updateIndustryTags, data, popupContext, onGetPageData],
  );

  const confirmSelectedTags = useCallback(
    (selectedList, tags, models) => {
      const industryCategoryIds = [...tags, ...models].map(category => category.id);

      if (industryCategoryIds.length === 0) return;

      const confirmMessage = [
        'You have chosen to add the following Industry Tags to the selected targets: \n',
        models
          .reduce((arr, { category }) => {
            arr.push(`${category} (Business Model)`);

            return arr;
          }, [])
          .join('\n'),
        tags
          .reduce((arr, { category, parentId }) => {
            if (parentId) {
              arr.push(`${category} (Sub Category)`);
            } else {
              arr.push(`${category} (Broad Industry Category)`);
            }

            return arr;
          }, [])
          .join('\n'),
        '\nIs this correct?',
      ].join('\n');

      // This is necessary to open "ConfirmPopup" after closing "TagsManagementPopup".
      setTimeout(() => {
        popupContext.openPopup('ConfirmPopup', {
          message: confirmMessage,
          onOk: () => handleUpdateIndustryTags(selectedList, industryCategoryIds),
          onCancel: () =>
            popupContext.openPopup('TagsManagementPopup', {
              addNew: false,
              callback: (tags, models) => confirmSelectedTags(selectedList, tags, models),
            }),
          yes: 'Add Tags',
          no: 'Cancel',
        });
      }, 0);
    },
    [popupContext, handleUpdateIndustryTags],
  );

  const handleSetActionsBase = useCallback(
    (currentPage, refreshGrid) => {
      popupContext.openPopup('SetNextActionsPopup', {
        currentPage,
        onUpdate: handleUpdateNextActions,
        onDelete: handleDeleteNextActions,
        refreshGrid,
      });
    },
    [popupContext, handleUpdateNextActions, handleDeleteNextActions],
  );

  const handleSetActions = useMemo(() => checkActionPermission(user, handleSetActionsBase, popupContext), [
    user,
    handleSetActionsBase,
    popupContext,
  ]);

  const handlePushNextActionDatesBase = useCallback(
    refreshGrid => {
      popupContext.openPopup('PushNextActionDatesPopup', {
        onUpdate: handleUpdateNextActionsDates,
        refreshGrid,
      });
    },
    [popupContext, handleUpdateNextActionsDates],
  );

  const handlePushNextActionDates = useMemo(
    () => checkActionPermission(user, handlePushNextActionDatesBase, popupContext),
    [user, handlePushNextActionDatesBase, popupContext],
  );

  const handleAddUpdateBuyerBase = useCallback(
    refreshGrid => {
      popupContext.openPopup('AddUpdateBuyerPopup', {
        onSave: handleSaveAddUpdateBuyer,
        refreshGrid,
      });
    },
    [popupContext, handleSaveAddUpdateBuyer],
  );

  const handleAddUpdateBuyer = useMemo(() => checkActionPermission(user, handleAddUpdateBuyerBase, popupContext), [
    user,
    handleAddUpdateBuyerBase,
    popupContext,
  ]);

  const handleInternalFlipBase = useCallback(
    (idList, refreshGrid) => {
      popupContext.openPopup('InternalFlipPopup', { idList, refreshGrid, hash: data.get('searchParamsHash') });
    },
    [popupContext, data],
  );

  const handleInternalFlip = useMemo(() => checkActionPermission(user, handleInternalFlipBase, popupContext), [
    user,
    handleInternalFlipBase,
    popupContext,
  ]);

  const handleDownloadExportBase = useCallback(
    selectedList => {
      const params = {
        requestParams,
        stateParams,
        hash: data.get('searchParamsHash'),
      };

      onDownloadExport(selectedList, params);
    },
    [requestParams, stateParams, data, onDownloadExport],
  );

  const handleDownloadExport = useMemo(() => checkActionPermission(user, handleDownloadExportBase, popupContext), [
    user,
    handleDownloadExportBase,
    popupContext,
  ]);

  const handleSaveQueryAdd = useCallback(
    (selectedList, params, name) => {
      const requestParamsCamelized = camelizeKeys(requestParams);

      handleAddSaveQuery(
        {
          params: { ...params, requestParams: requestParamsCamelized, stateParams },
          name,
          type: 'windward',
        },
        {
          afterSuccess: () => showInformation(popupContext.openPopup, 'Query saved successfully.'),
          afterError: ({ responseBody }) => {
            popupContext.endPopupLoading(responseBody);
          },
        },
      );
    },
    [requestParams, handleAddSaveQuery, stateParams, popupContext],
  );

  const handleSetBacklogBase = useCallback(
    selectedIds => {
      setBacklog({
        body: {
          params: {
            selected_ids: selectedIds,
            hash: data.get('searchParamsHash'),
          },
        },
        callback: {
          beforeStart: popupContext.startPopupLoading,
          afterSuccess: ({ response: { list } }) => {
            const countSkipped = selectedIds.length - list.skipE.length - list.skipNA.length - list.skipRF.length;

            const header = `${countSkipped} out of ${selectedIds.length} Companies have been backlogged, including:`;

            const popupMessages = [];

            const backloggedActivitiesList = backloggedActivities.toJS();

            const backLoggerMessage = backloggedActivitiesList
              .map(camelize)
              .reduce((accum, key, index) => {
                if (!Object.prototype.hasOwnProperty.call(list, key)) return accum;

                accum.push(`${list[key]} ${backloggedActivitiesList[index]}'s`);

                return accum;
              }, [])
              .join('\n');

            if (backLoggerMessage) {
              popupMessages.push(backLoggerMessage);
            }

            if (list.skipNA.length) {
              const missingTitle =
                'The following companies have been skipped because they do not have Next Actions that can be Backlogged: \n';
              let skipMessage = '';

              list.skipNA.forEach(error => {
                const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
                const companyName = `${error.companyName} ${link}`;

                skipMessage = `${skipMessage} ${companyName} \n`;
              });
              popupMessages.push(`${missingTitle} ${skipMessage}`);
            }

            if (list.skipRF.length) {
              const missingTitle = 'The following profiles were missing information: \n';
              let skipMessage = '';

              list.skipRF.forEach(error => {
                let errors = '';
                const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
                const companyName = `${error.companyName} ${link}\n`;

                error.emptyRequiredFields.forEach(message => {
                  errors += `- ${message}.\n`;
                });
                skipMessage = `${skipMessage} ${companyName} ${errors}`;
              });
              popupMessages.push(`${missingTitle} ${skipMessage}`);
            }

            if (list.skipE.length) {
              const missingTitle = 'The following companies have been skipped because: \n';
              let skipMessage = '';

              list.skipE.forEach(error => {
                const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
                const errorLink = `${error.error} ${link}\n`;

                skipMessage = `${skipMessage} ${errorLink}`;
              });
              popupMessages.push(`${missingTitle} ${skipMessage}`);
            }

            popupContext.endPopupLoading();
            showInformationCustomTitle(popupContext.openPopup, header, popupMessages, () => {
              popupContext.closePopup();
            });
            onGetPageData(1, true);
          },
          afterError: () => {
            popupContext.endPopupLoading();
            showError(popupContext.openPopup, ['Set backlog is fail. Please try again!']);
          },
        },
      });
    },
    [setBacklog, data, popupContext, backloggedActivities, onGetPageData],
  );

  const handleSetBacklog = useMemo(() => checkActionPermission(user, handleSetBacklogBase, popupContext), [
    user,
    handleSetBacklogBase,
    popupContext,
  ]);

  const handleRunWindwardReportBase = useCallback(
    ({ selectedList, selectedUserId, nameTemplate, name, templateParams, searchBuyerId, searchFields }) => {
      const selectedTargets = selectedList.map(id =>
        data
          .get('queryResults')
          .find(object => object.get('id') === id)
          .toJS(),
      );
      const buyersSelectedListParam = data.getIn(['buyer', 'selectedListParam'])?.toJS();

      runWindwardReport({
        name,
        selectedUserId,
        nameTemplate,
        templateParams,
        selectedTargets,
        searchBuyerId,
        searchParams: decamelizeKeys(requestParams),
        afterSuccess: ({ response: { data } }) => {
          const message = Object.entries(searchFields)
            .filter(([, value]) => value.required)
            .map(([key, value]) => {
              const decamelizedKey = decamelize(key);
              const selectedBuyers = buyersSelectedListParam.map(buyer => ({
                project_id: buyer.projectId,
                target_id: buyer.id,
                buyer_id: buyer.buyerId,
                search_buyer_id: searchBuyerId,
                search_project_id: buyer.projectId,
                search_app_list_id: buyer.approvalListId,
                search_priority: buyer.priority,
                search_approved: buyer.approved,
                search_active_buyer: buyer.active,
                search_cs_from: buyer.currentStatusRange?.min,
                search_cs_to: buyer.currentStatusRange?.max,
                search_hs_from: buyer.highStatusRange?.min,
                search_hs_to: buyer.highStatusRange?.max,
              }));

              const duplicateCount = Object.values(
                selectedBuyers.reduce(accumulator => {
                  accumulator[decamelizedKey] = (accumulator[decamelizedKey] ?? 0) + 1;

                  return accumulator;
                }, {}),
              )[0];

              if (duplicateCount > 1 && value.required) {
                return value.text;
              }

              return '';
            });

          const dataWithWarning = {
            ...data,
            warning:
              message[0] !== '' ? `This report currently only supports ${message.join('/')} at a time.` : undefined,
          };

          return successWindwardGeneration(dataWithWarning, popupContext);
        },
        afterError: () => popupContext.endPopupLoading(),
      });
    },
    [data, runWindwardReport, requestParams, popupContext],
  );

  const handleRunWindwardReport = useMemo(
    () => checkActionPermission(user, handleRunWindwardReportBase, popupContext),
    [user, handleRunWindwardReportBase, popupContext],
  );

  const handleAssignTradeShow = useCallback(
    (idList, refreshGrid) => {
      popupContext.openPopup('AssignTradeShowPopup', {
        targetsIds: idList,
        refreshGrid,
        hash: data.get('searchParamsHash'),
      });
    },
    [popupContext, data],
  );

  const handleConfirmMerging = useCallback(
    (tag, refreshGrid) => {
      onConfirmMerging({
        body: {
          params: {
            selected_ids: handleGetSelectedIds(),
            select_all: false,
          },
        },
        callback: {
          beforeStart: popupContext.startPopupLoading(),
          afterSuccess: () => {
            popupContext.endPopupLoading();
            refreshGrid();
          },
          afterError: () => {
            popupContext.endPopupLoading();
          },
        },
      });
    },
    [onConfirmMerging, handleGetSelectedIds, popupContext],
  );

  const handleContextMenuItemClick = useCallback(
    (event, { key }, params) => {
      event.preventDefault();

      const selectList = data.get('selectList');
      const limit = hasAccess() ? selectList.size : 1000;

      let includedBuyers = null;

      if (data.getIn(['buyer', 'selectedList'])) {
        const buyersSelectedList = data.getIn(['buyer', 'selectedList']).toJS();
        const buyersSelectedListParam = data.getIn(['buyer', 'selectedListParam']).toJS();

        includedBuyers = buyersSelectedListParam.filter((_, index) => !buyersSelectedList[index].exclude);
      }

      const selectedList = selectList.slice(0, limit).reduce((accumulator, item) => {
        accumulator.push(item.get('id'));

        return accumulator;
      }, []);

      const refreshGrid = () => {
        onGetPageData(1, false);
      };

      switch (key) {
        case config.actionList.getIn([0, 'key']):
          popupContext.openPopup('AddHarvcoTagPopup', {
            onAddTag: handleAddTag,
            refreshGrid,
          });
          break;

        case config.actionList.getIn([1, 'key']):
          handleSetActions('browse', refreshGrid);
          break;

        case config.actionList.getIn([2, 'key']):
          handlePushNextActionDates(refreshGrid);
          break;

        case config.actionList.getIn([3, 'key']):
          handleAddUpdateBuyer(refreshGrid);
          break;

        case config.actionList.getIn([4, 'key']):
          handleInternalFlip(selectedList, refreshGrid);
          break;

        case config.actionList.getIn([5, 'key']):
          handleDownloadExport(selectedList);
          break;

        case config.actionList.getIn([6, 'key']):
          popupContext.openPopup('DeleteConfirmPopup', {
            delete: () => handleDelete(onDelete, DELETE_SUCCESSFULLY_TEXT),
            title: 'Delete target(s)',
          });
          break;

        case config.actionList.getIn([7, 'key']):
          popupContext.openPopup('DeleteConfirmPopup', {
            delete: () => handleDelete(onDeleteConfirmation, DELETION_REQUEST_TEXT),
            message:
              'Are you sure you want to request your supervisor to delete selected database record(s)? After the supervisor approval, the records and all associate data will be permanently deleted.',
            title: 'Delete target(s)',
          });
          break;

        case config.actionList.getIn([8, 'key']):
          popupContext.openPopup('TagsManagementPopup', {
            addNew: false,
            callback: (tags, models) => confirmSelectedTags(selectedList, tags, models),
          });
          break;

        case config.actionList.getIn([9, 'key']):
          popupContext.openPopup('ConfirmPopup', {
            message: `You are about to Backlog ${selectedList.length} Target(s). Are you sure you want to proceed?`,
            yes: 'Yes',
            no: 'No',
            onOk: () => {
              handleSetBacklog(selectedList);
              popupContext.closePopup();
            },
            onCancel: () => popupContext.closePopup(),
          });
          break;

        case config.actionList.getIn([10, 'key']):
          popupContext.openPopup('RunWindwardReportPopup', {
            windward: params,
            onAddSaveQuery: (queryParams, queryName) => {
              handleSaveQueryAdd(selectedList, queryParams, queryName);
            },
            callback: (selectedUserId, nameTemplate, templateParams, searchFields) => {
              if (includedBuyers) {
                const decamelizeIncludedBuyers = decamelizeKeys(includedBuyers);
                const searchBuyerId =
                  (decamelizeIncludedBuyers.length > 0 && decamelizeIncludedBuyers[0].buyer_id) || null;

                return handleRunWindwardReport({
                  selectedList,
                  selectedUserId,
                  nameTemplate,
                  name,
                  templateParams,
                  searchBuyerId,
                  searchFields,
                });
              }

              return handleRunWindwardReport({
                selectedList,
                selectedUserId,
                nameTemplate,
                name,
                templateParams,
                searchFields,
              });
            },
          });
          break;

        case config.actionList.getIn([11, 'key']):
          handleAssignTradeShow(selectedList, refreshGrid);
          break;

        case config.actionList.getIn([12, 'key']):
          popupContext.openPopup('ConfirmPopup', {
            message:
              'Are you really sure you want to confirm merge for selected companies?\nThis operation can not be reverted.',
            yes: 'Yes',
            no: 'No',
            onOk: () => {
              popupContext.startPopupLoading();
              handleConfirmMerging(selectedList, refreshGrid);
              popupContext.closePopup();
            },
            onCancel: () => popupContext.closePopup(),
          });
          break;

        default:
          break;
      }
    },
    [
      data,
      hasAccess,
      onGetPageData,
      popupContext,
      handleAddTag,
      handleSetActions,
      handlePushNextActionDates,
      handleAddUpdateBuyer,
      handleInternalFlip,
      handleDownloadExport,
      handleDelete,
      onDelete,
      onDeleteConfirmation,
      confirmSelectedTags,
      handleSetBacklog,
      handleSaveQueryAdd,
      handleRunWindwardReport,
      name,
      handleAssignTradeShow,
      handleConfirmMerging,
    ],
  );

  const handleSort = useCallback(
    sortModel => {
      handleSortModelChanged(sortModel, data, onChange);
    },
    [data, onChange],
  );

  const handleSelectionChanged = useCallback(() => {
    const selectedRows = gridApiRef.current.getSelectedRows();

    if (onSelectRows && selectedRows.length > 1) {
      onSelectRows({
        name,
        rowIndexs: selectedRows.map(row => row.order),
      });
      setTimeout(() => {
        gridApiRef.current.deselectAll();
      }, 2000);
    }
  }, [onSelectRows, name]);

  const handleSelectionReset = useCallback(
    event => {
      event.preventDefault();

      handleResetSelection({
        name,
      });
    },
    [handleResetSelection, name],
  );

  const handleShareLinkClick = useCallback(() => {
    const requestParamsCamelized = camelizeKeys(requestParams);

    popupContext.openPopup('ShareLinkPopup', {
      requestParams: requestParamsCamelized,
      stateParams,
      name,
    });
  }, [requestParams, stateParams, name, popupContext]);

  const handleAddSaveQueryClick = useCallback(() => {
    const requestParamsCamelized = camelizeKeys(requestParams);

    popupContext.openPopup('SaveQueryPopup', {
      onAddSaveQuery: queryName =>
        handleAddSaveQuery(
          { params: { requestParams: requestParamsCamelized, stateParams }, name: queryName, type: 'search' },
          {
            afterSuccess: () => showInformation(popupContext.openPopup, 'Query saved successfully.'),
            afterError: ({ responseBody }) => {
              popupContext.endPopupLoading(responseBody);
            },
          },
        ),
    });
  }, [requestParams, stateParams, handleAddSaveQuery, popupContext]);

  const gridColumnDefs = useMemo(
    () =>
      columnDefs.map(columnDef => {
        const field = columnDef.get('field');

        switch (field) {
          case 'selected':
            return columnDef.set('cellRendererFramework', CheckboxCellRenderer).set('onRowChange', onRowChange);

          case 'approved':
            return columnDef.set('cellRendererFramework', ApprovedCellRenderer);

          case 'nextAction':
            return columnDef.set('cellRendererFramework', NextActionCellRenderer);

          case 'employed':

          // fallthrough
          case 'resume':

          // fallthrough
          case 'needsFund':
            return columnDef.set('cellRendererFramework', Colored);

          case 'nextActionDate':

          // fallthrough
          case 'lastActionDate':

          // fallthrough
          case 'lastResearchedDate':
            return columnDef.set('cellRendererFramework', Substring);

          case 'projectName':
            return columnDef.get('renderType') !== 'projectGroup'
              ? columnDef
              : columnDef.set('cellRendererFramework', ProjectCell);

          case 'description':
            return columnDef.set('cellRendererFramework', Titled);

          default:
            return columnDef;
        }
      }),
    [columnDefs, onRowChange],
  );

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;

      return;
    }

    const isFetching = data.get('isFetching');

    if (gridApiRef.current && prevIsFetchingRef.current === true && isFetching === false) {
      gridApiRef.current.refreshCells({ force: true });
    }

    prevIsFetchingRef.current = isFetching;

    if (selectedAllOnce === false) {
      const searchParams = new URLSearchParams(location.search);
      const openWindwardReport = searchParams.has('openWindwardReport');
      const preFill = searchParams.has('preFill');
      const queryResults = data.get('queryResults');

      if (openWindwardReport && queryResults.size > 0) {
        setSelectedAllOnce(true);
        handleSelectAll(new Event('click'));

        // Need to wait selection ids.
        setTimeout(() => {
          const windwardReportParams = preFill ? data.get('windwardReport') : undefined;

          handleContextMenuItemClick(
            new Event('click'),
            { key: config.actionList.getIn([10, 'key']) },
            windwardReportParams,
          );
        }, 1000);
      }
    }
  });

  const selectedNumber = data.get('selectList').size;
  const selectedContent =
    selectedNumber > 0 ? (
      <span className={style.results}>
        {selectedNumber} selected
        <a className={classnames(style.control, 'ml10')} href="#reset" onClick={handleSelectionReset}>
          Reset Selection
        </a>
      </span>
    ) : null;

  const mergeResultOnlyChecked = data.getIn(['mergeResultOnly', 'checked']);

  const actionContents = contextActionList
    .filter(action => {
      if (name === 'buyers') {
        return ['addTagToSelected', 'exportSelected', 'deleteSelected', 'mergeConfirm'].includes(action.key);
      }

      if (name === 'executives') {
        return ['addTagToSelected', 'exportSelected'].includes(action.key);
      }

      return action;
    })
    .filter(action => {
      // display "Merge Confirm" checkbox
      if (action.key === 'mergeConfirm' && onConfirmMerging) {
        if (mergeResultOnlyChecked === false) {
          return null;
        }
      }

      if (action.key === 'mergeConfirm' && !onConfirmMerging) {
        return null;
      }

      return action;
    })
    .map(({ attributes, key, name: actionName }) => (
      <DropDownElement
        {...attributes}
        key={key}
        onClick={event => handleContextMenuItemClick(event, { key }, undefined)}
      >
        {actionName}
      </DropDownElement>
    ));

  const contextDropDownClass = classnames({
    'btn btn-default btn-xs': true,
    hide: hideContextDropDown === true,
  });

  const selectClass = classnames({
    [style.control]: true,
    hide: hideContextDropDown === true,
  });

  const searchButtonClasses = classnames('btn-primary', { 'btn-disabled': !hasParams });

  const cancelButtonClasses = classnames('btn-default', { 'btn-disabled': !data.get('isFetching') });

  const massSelectProps = name === 'targets' || name === 'executives' ? { rowSelection: 'multiple' } : {};

  const renderShareButton = (name === 'targets' || name === 'buyers' || name === 'executives') && (
    <>
      <Button onClick={handleShareLinkClick}>
        <span className="fa fa-share" />
        Share link
      </Button>
      <Button className="btn-primary" onClick={handleAddSaveQueryClick}>
        <span className="fa fa-save" />
        Save query
      </Button>
    </>
  );

  return (
    <Card className={style.host} flush>
      <div className={style.header}>
        <div className={style.selection}>
          <a className={selectClass} href="#" onClick={handleSelectAll}>
            Select all
          </a>
          <a className={selectClass} href="#" onClick={handleDeselectAll}>
            Select none
          </a>
          <DropDown
            buttonContent="Actions"
            className={contextDropDownClass}
            disabled={selectedNumber === 0}
            id="actionDropDown"
          >
            {actionContents}
          </DropDown>
          <span className={style.results}>{data.get('totalRecord')} results</span>
          {selectedContent}
        </div>
        <div className={style.buttons}>
          <Button className={searchButtonClasses} onClick={() => onGetPageData(1, false)}>
            <span className="fa-search fa" />
            Search
          </Button>
          <Button className={cancelButtonClasses} onClick={() => onAbortRequest(name)}>
            <span className="fa-ban fa" />
            Cancel
          </Button>
          <Button onClick={() => onGetPageData(1, false)}>
            <span className="fa-refresh fa" />
            Refresh
          </Button>
          <Button onClick={() => onClearFilter(name)}>
            <span className="fa-times fa" />
            Clear
          </Button>
          {renderShareButton}
          {onToggle && (
            <IconButton
              icon={data.get('showTabControls') ? 'caret-right' : 'caret-left'}
              onClick={onToggle}
              size="sm"
              title={data.get('showTabControls') ? 'Hide filters' : 'Show filters'}
            />
          )}
        </div>
      </div>
      <div className={classnames(style.grid, 'ag-theme-fresh')}>
        <Grid
          {...massSelectProps}
          columnDefs={gridColumnDefs}
          getRowNodeId={getRowNodeId}
          isFetching={data.get('isFetching')}
          onCellClicked={handleCellClicked}
          onGetNextPageData={onGetNextPageData}
          onGridReady={onGridReady}
          onRowDoubleClicked={handleRowDoubleClicked}
          onSelectionChanged={handleSelectionChanged}
          onSortModelChanged={handleSort}
          page={data.get('page')}
          rowData={data.get('queryResults')}
          suppressMovableColumns={false}
          totalPages={data.get('totalPages')}
          serverSideSorting
          sortable
        />
      </div>
    </Card>
  );
};

BrowseTable.propTypes = {
  columnDefs: PropTypes.instanceOf(List).isRequired,
  data: PropTypes.instanceOf(Map).isRequired,
  handleUpdateSelectedRow: PropTypes.func,
  hideContextDropDown: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onAddTag: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onConfirmMerging: PropTypes.func,
  onDelete: PropTypes.func,
  onDownloadExport: PropTypes.func,
  onGetNextPageData: PropTypes.func.isRequired,
  onPushNextActionsDates: PropTypes.func,
  onSaveAddUpdateBuyer: PropTypes.func,
  onSearchParamsBeforeBrowse: PropTypes.func.isRequired,
  onSetNextActions: PropTypes.func,
  onUpdateQueryResult: PropTypes.func,
  setBacklog: PropTypes.func,
  updateIndustryTags: PropTypes.func,
};

const mapStateToProps = (state, props) => ({
  ...props,
  user: state.auth.get('user', null),
  backloggedActivities: state.eventActivities.get('backloggedActivityValues', List()),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    {
      handleAddSaveQuery,
    },
    dispatch,
  ),
});

export { BrowseTable };
export default connect(mapStateToProps, mapDispatchToProps)(memo(BrowseTable));
